/**
 * Сессия подбора и чат: `/api/v1/search-sessions/*` (BE-076).
 *
 * Все запросы персональны и требуют cookie-сессии; ownership проверяет backend,
 * чужая сессия неотличима от несуществующей (404). Изменяющие запросы уходят с
 * CSRF-заголовком — его подставляет `apiFetch`.
 *
 * Две вещи, ради которых здесь больше кода, чем «просто fetch»:
 *
 * * **идемпотентность.** Создание сессии — по `Idempotency-Key`, сообщение —
 *   по `client_message_id`. Повтор после обрыва сети не создаёт ни второй
 *   сессии, ни второго сообщения (MVP_TECH_RFC §21, §10);
 * * **оптимистичное сообщение.** Ответ реальной модели занимает секунды, и
 *   пустой экран на это время выглядел бы как потерянный ввод. Сообщение
 *   пользователя появляется сразу, а ответ сервера заменяет его по
 *   `client_message_id`.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiFetch } from '@shared/api/client';
import { ApiError } from '@shared/api/problem';
import { queryKeys } from '@shared/api/queryKeys';
import { i18n, toSupportedLocale } from '@shared/i18n';
import { randomUuid } from '@shared/lib/uuid';

import type { OrderStage } from './stage';
import type {
  ArchiveCandidate,
  ChatMessage,
  ChatTurn,
  CriteriaPatchOperation,
  SearchLimitContext,
  SearchSession,
  SearchSessionSort,
  SearchSessionStatus,
  SearchSessionsPage,
  SessionState,
} from './types';

/** MVP_TECH_RFC §22: состояние сессии живёт в кеше 15 секунд. */
export const SEARCH_SESSION_STALE_TIME_MS = 15_000;

/**
 * Собственный timeout сообщения: значение по умолчанию (15 с) обрывало бы
 * живые ответы модели.
 *
 * Значение выбрано **больше** бюджета backend (`LLM_TIMEOUT_SECONDS` × попытки).
 * Клиент, сдающийся раньше сервера, — источник расхождений: пользователь видит
 * «не отправлено», продолжает диалог, а запоздавший ответ приходит к уже
 * изменившимся критериям.
 */
const MESSAGE_TIMEOUT_MS = 150_000;

/**
 * Ключ идемпотентности создания запроса переживает перезагрузку вкладки.
 *
 * Без него повтор после обрыва сети создавал бы **второй** запрос и съедал
 * слот из десяти: пользователь терял бы место в списке из-за собственного F5
 * (MVP_TECH_RFC §21).
 */
const BOOTSTRAP_KEY_STORAGE = 'carviq.search-session-key';

function bootstrapKey(): string {
  try {
    const stored = sessionStorage.getItem(BOOTSTRAP_KEY_STORAGE);
    if (stored) return stored;
    const created = randomUuid();
    sessionStorage.setItem(BOOTSTRAP_KEY_STORAGE, created);
    return created;
  } catch {
    // Приватный режим Safari запрещает запись: идемпотентность в пределах
    // одного вызова всё равно обеспечивается, между перезагрузками — нет.
    return randomUuid();
  }
}

function clearBootstrapKey(): void {
  try {
    sessionStorage.removeItem(BOOTSTRAP_KEY_STORAGE);
  } catch {
    // См. комментарий в bootstrapKey.
  }
}

// --- Список запросов (FE-150) -----------------------------------------------------------

export interface SearchSessionsQuery {
  status?: readonly SearchSessionStatus[] | undefined;
  /** Фильтр по стадии воронки (BE-200). Значения — из `ORDER_STAGES`. */
  stage?: readonly OrderStage[] | undefined;
  sort?: SearchSessionSort | undefined;
  page?: number | undefined;
}

/** Параметры запроса списка. Набор детерминированный: он же входит в ключ кеша. */
export function searchSessionsParams(query: SearchSessionsQuery): [string, string][] {
  const params: [string, string][] = [];
  for (const value of query.status ?? []) params.push(['status', value]);
  for (const value of query.stage ?? []) params.push(['stage', value]);
  if (query.sort) params.push(['sort', query.sort]);
  if (query.page !== undefined && query.page > 1) params.push(['page', String(query.page)]);
  return params;
}

export function useSearchSessions(
  query: SearchSessionsQuery,
  enabled = true,
): UseQueryResult<SearchSessionsPage> {
  const params = searchSessionsParams(query);

  return useQuery({
    queryKey: queryKeys.searchSessions(Object.fromEntries(params)),
    queryFn: ({ signal }) => {
      const search = new URLSearchParams(params).toString();
      return apiFetch<SearchSessionsPage>(`/search-sessions${search ? `?${search}` : ''}`, {
        signal,
      });
    },
    enabled,
    staleTime: SEARCH_SESSION_STALE_TIME_MS,
    // Смена сортировки не должна мигать скелетоном поверх готового списка.
    placeholderData: (previous) => previous,
  });
}

// --- Чтение одного запроса --------------------------------------------------------------

/**
 * Состояние запроса: диалог, критерии, ссылка на прогон.
 *
 * `404` — не сбой сети, а «запроса нет или он чужой» (§27). Разбирать этот
 * случай обязан вызывающий экран: пустая страница и «ошибка загрузки» — разные
 * ответы пользователю.
 */
export function useSearchSession(
  searchId: string | null,
  enabled = true,
): UseQueryResult<SessionState> {
  return useQuery({
    queryKey: queryKeys.searchSession(searchId ?? ''),
    queryFn: ({ signal }) =>
      apiFetch<SessionState>(`/search-sessions/${encodeURIComponent(searchId ?? '')}`, { signal }),
    enabled: enabled && searchId !== null,
    staleTime: SEARCH_SESSION_STALE_TIME_MS,
  });
}

// --- Создание, переименование, архив, удаление (FE-152) ---------------------------------

export interface CreateSessionInput {
  /** Запрос с главной. Сообщением он уходит отдельно — так его видно в чате. */
  initialPrompt?: string | null;
}

/**
 * Язык, выбранный в переключателе интерфейса.
 *
 * Берётся из самого i18next, а не из параметра: создать заказ можно из двух
 * мест, и второй способ рано или поздно забыли бы передать язык — тогда Karl
 * заговорил бы по-немецки ровно на одном из путей.
 */
function currentLocale(): string {
  return toSupportedLocale(i18n.resolvedLanguage ?? i18n.language);
}

/**
 * Разобрать `409 SEARCH_LIMIT_REACHED`.
 *
 * Список кандидатов приходит от backend уже отсортированным по давности
 * активности; фронт его только показывает. **Ни один запрос не архивируется
 * автоматически** — это выбор пользователя (MVP_PRD §20, AC-08).
 */
export function searchLimitContext(error: unknown): SearchLimitContext | null {
  if (!(error instanceof ApiError) || error.code !== 'SEARCH_LIMIT_REACHED') return null;
  const context = error.context;
  if (context === null) return null;
  const candidates = context['archive_candidates'];
  return {
    limit: typeof context['limit'] === 'number' ? context['limit'] : 0,
    archive_candidates: Array.isArray(candidates) ? (candidates as ArchiveCandidate[]) : [],
  };
}

export function useCreateSearchSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateSessionInput) =>
      apiFetch<SessionState>('/search-sessions', {
        method: 'POST',
        idempotencyKey: bootstrapKey(),
        // Язык заказа — выбор человека в переключателе интерфейса, а не
        // `Accept-Language` (BE-076). Без него Karl отвечал бы на языке по
        // умолчанию: у гостя профиля нет вовсе, а профиль пользователя хранит
        // язык, с которым он когда-то регистрировался.
        body: { initial_prompt: input.initialPrompt ?? null, locale: currentLocale() },
      }),
    onSuccess: (state) => {
      clearBootstrapKey();
      queryClient.setQueryData(queryKeys.searchSession(state.session.id), state);
      void queryClient.invalidateQueries({ queryKey: ['search-sessions'] });
    },
  });
}

export interface PatchSearchSessionInput {
  searchId: string;
  /** Версия строки, которую видел пользователь: несовпадение backend отклонит. */
  version: number;
  /** `null` — вернуть автоматическое название; поле не передано — не трогать. */
  title?: string | null | undefined;
  archived?: boolean | undefined;
}

/**
 * Переименование и архивирование.
 *
 * Оптимистичного UI здесь нет намеренно: и переименование, и архивирование
 * проходят через оптимистическую блокировку, а возврат из архива может упереться
 * в лимит и вернуть `409`. Нарисовать результат заранее означало бы показать
 * состояние, которого на сервере не случилось.
 */
export function usePatchSearchSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ searchId, version, title, archived }: PatchSearchSessionInput) => {
      const body: Record<string, unknown> = { version };
      // `title` кладётся в тело, только если его действительно меняют:
      // «не передали» и «передали null» — разные операции (BE-151).
      if (title !== undefined) body['title'] = title;
      if (archived !== undefined) body['archived'] = archived;
      return apiFetch<SearchSession>(`/search-sessions/${encodeURIComponent(searchId)}`, {
        method: 'PATCH',
        body,
      });
    },
    onSuccess: (session) => {
      void queryClient.invalidateQueries({ queryKey: ['search-sessions'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.searchSession(session.id) });
    },
  });
}

/**
 * Удаление запроса вместе с диалогом, критериями и результатами.
 *
 * Переговоры по нему сохраняются: письма продавцу уже отправлены, и удаление
 * запроса не может их отменить (MVP_PRD §20). Об этом сказано в подтверждении.
 */
export function useDeleteSearchSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (searchId: string) =>
      apiFetch<undefined>(`/search-sessions/${encodeURIComponent(searchId)}`, {
        method: 'DELETE',
      }),
    onSuccess: (_result, searchId) => {
      queryClient.removeQueries({ queryKey: queryKeys.searchSession(searchId) });
      void queryClient.invalidateQueries({ queryKey: ['search-sessions'] });
    },
  });
}

// --- Сообщение чата ---------------------------------------------------------------------

export interface SendMessageInput {
  /** Стабильный при повторе UUID: второй раз тот же ID не создаёт дубликат. */
  clientMessageId: string;
  content: string;
  /**
   * Коды нажатых кнопок текущего вопроса.
   *
   * Backend применяет их детерминированно, без обращения к модели: ответ на
   * заданный вопрос однозначен, и извлекать его заново из текста незачем
   * (BE-072). Свободный текст уходит с пустым списком.
   */
  quickReplyCodes?: string[] | undefined;
}

/**
 * Слить ответ сервера с кешем.
 *
 * Совпадение ищется и по `id`, и по `client_message_id`: оптимистичное
 * сообщение живёт под временным идентификатором и обязано быть заменено, а не
 * продублировано.
 */
export function mergeMessages(
  messages: readonly ChatMessage[],
  incoming: readonly ChatMessage[],
): ChatMessage[] {
  const result = [...messages];
  for (const message of incoming) {
    const index = result.findIndex(
      (item) =>
        item.id === message.id ||
        (message.client_message_id !== null &&
          item.client_message_id === message.client_message_id),
    );
    if (index === -1) {
      result.push(message);
    } else {
      result[index] = message;
    }
  }
  return result.sort((left, right) => left.sequence_number - right.sequence_number);
}

function optimisticMessage(state: SessionState, input: SendMessageInput): ChatMessage {
  const last = state.messages.at(-1);
  return {
    id: `pending:${input.clientMessageId}`,
    role: 'user',
    content: input.content,
    // Реплики пользователя формулирует пользователь: поле есть только у Karl.
    rendered_text: null,
    status: 'processing',
    sequence_number: (last?.sequence_number ?? 0) + 1,
    client_message_id: input.clientMessageId,
    criteria_version_id: null,
    metadata: {},
    created_at: new Date().toISOString(),
    completed_at: null,
  };
}

export function useSendChatMessage(sessionId: string | null) {
  const queryClient = useQueryClient();
  const key = queryKeys.searchSession(sessionId ?? '');

  return useMutation({
    mutationFn: (input: SendMessageInput) => {
      if (sessionId === null) throw new Error('sendChatMessage requires an active session');
      return apiFetch<ChatTurn>(`/search-sessions/${encodeURIComponent(sessionId)}/messages`, {
        method: 'POST',
        timeoutMs: MESSAGE_TIMEOUT_MS,
        body: {
          client_message_id: input.clientMessageId,
          content: input.content,
          quick_reply_codes: input.quickReplyCodes ?? [],
        },
      });
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<SessionState | null>(key) ?? null;
      if (previous) {
        queryClient.setQueryData<SessionState>(key, {
          ...previous,
          messages: mergeMessages(previous.messages, [optimisticMessage(previous, input)]),
        });
      }
      return { previous };
    },
    onSuccess: (turn) => {
      queryClient.setQueryData<SessionState | null>(key, (current) => {
        if (!current) return null;
        const incoming = turn.assistant_message
          ? [turn.user_message, turn.assistant_message]
          : [turn.user_message];
        return {
          ...current,
          messages: mergeMessages(current.messages, incoming),
          criteria: turn.criteria,
          // Предложение пересобрать подбор приходит вместе с ответом: без него
          // оно ждало бы следующего чтения заказа, и правка критериев снова
          // выглядела бы как ничего не делающая (BE-355).
          rebuild_pending: turn.rebuild_pending,
        };
      });

      // Правка критериев меняет на сервере `status` и название запроса, а в
      // ответе на сообщение их нет: карточка в списке и трекер обязаны это
      // увидеть. `exact` обязателен — ключ заказа является префиксом ключей
      // четвёрки и сравнения, а они от сообщения не меняются: перестройку
      // запускает подтверждение, а не текст.
      if (turn.changes.length > 0) {
        void queryClient.invalidateQueries({ queryKey: key, exact: true });
        void queryClient.invalidateQueries({ queryKey: ['search-sessions'] });
      }
    },
    onError: (error, _input, context) => {
      // 409 «предыдущее сообщение ещё обрабатывается» и конфликты версий
      // разрешает сервер: возвращаем снимок и перечитываем состояние.
      queryClient.setQueryData(key, context?.previous ?? null);
      if (error instanceof ApiError && error.status === 409) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });
}

// --- Ручная правка критериев ------------------------------------------------------------

export interface PatchCriteriaInput {
  /** Версия, которую видел пользователь: оптимистическая проверка сервера. */
  criteriaVersionId: string;
  operations: CriteriaPatchOperation[];
}

/**
 * `PATCH /criteria` — правка с экрана критериев и резервной формы.
 *
 * Вручную акцент можно только **снять** (`emphasis.* = normal`): назначить его
 * без цитаты пользователя backend не даст (MATCHING §4.2). При устаревшей
 * версии приходит `409 CRITERIA_VERSION_MISMATCH` — состояние перечитывается.
 */
export function usePatchCriteria(sessionId: string | null) {
  const queryClient = useQueryClient();
  const key = queryKeys.searchSession(sessionId ?? '');

  return useMutation({
    mutationFn: (input: PatchCriteriaInput) => {
      if (sessionId === null) throw new Error('patchCriteria requires an active session');
      return apiFetch<SessionState>(`/search-sessions/${encodeURIComponent(sessionId)}/criteria`, {
        method: 'PATCH',
        body: {
          criteria_version_id: input.criteriaVersionId,
          operations: input.operations.map((item) => ({ path: item.path, value: item.value })),
        },
      });
    },
    onSuccess: (state) => {
      queryClient.setQueryData(key, state);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });
}
